import React, { useState } from 'react';
import { PROVINCE_TAX_RATES, TAX_MODULE_DEFAULTS, calculateCanadianTax } from './canadianTax';
import { ContextualAgencyTrigger } from '../components/dashboard/AgencyUpsellManager.jsx';

const PROVINCES = Object.entries(PROVINCE_TAX_RATES).map(([code, r]) => ({ code, name: r.name, taxType: r.taxType }));

const TAX_TYPE_BADGE = {
    'hst':      { label: 'HST',         bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    'gst+pst':  { label: 'GST + PST',   bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
    'gst+qst':  { label: 'GST + QST',   bg: '#fdf4ff', color: '#6b21a8', border: '#e9d5ff' },
    'gst':      { label: 'GST Only',    bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
};

const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: '7px',
    border: '1px solid #cbd5e1', fontSize: '0.875rem', background: '#fff',
    boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#475569', marginBottom: '4px' };
const sectionCard = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' };

export const CanadianTaxManager = ({ config, onSave }) => {
    const modules = config?.modules || {};
    const taxCfg  = { ...TAX_MODULE_DEFAULTS, ...(modules.canadianTax || {}) };

    const [cfg, setCfg]         = useState(taxCfg);
    const [previewProv, setPreviewProv] = useState('ON');
    const [saved, setSaved]     = useState('');

    const update = (patch) => setCfg(prev => ({ ...prev, ...patch }));

    const updateCustomRate = (code, field, value) => {
        const existing = cfg.customRates?.[code] || {};
        update({ customRates: { ...cfg.customRates, [code]: { ...existing, [field]: parseFloat(value) || 0 } } });
    };

    const handleSave = () => {
        const newConfig = { ...config, modules: { ...modules, canadianTax: cfg } };
        onSave(newConfig);
        setSaved('✅ Canadian Tax settings saved!');
        setTimeout(() => setSaved(''), 3000);
    };

    const storeRates  = PROVINCE_TAX_RATES[cfg.storeProvince] || {};
    const previewResult = calculateCanadianTax(100, previewProv, cfg);
    const previewBadge  = TAX_TYPE_BADGE[PROVINCE_TAX_RATES[previewProv]?.taxType] || TAX_TYPE_BADGE['gst'];

    return (
        <div style={{ maxWidth: '780px', fontFamily: 'Inter, system-ui, sans-serif', display: 'flex', flexDirection: 'column', gap: '22px' }}>

            {/* Header */}
            <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '1.35rem', color: '#0f172a', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🍁 Canadian Tax Engine
                </h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.88rem', lineHeight: '1.55' }}>
                    Automatically calculate and display GST, HST, PST, and QST based on the store's registered province and the customer's shipping address. Follows CRA "place of supply" rules.
                </p>
                <ContextualAgencyTrigger context="ecommerce_tax" config={config} />
            </div>

            {/* Master Enable Toggle */}
            <div style={{
                ...sectionCard,
                border: `2px solid ${cfg.enabled ? '#10b981' : '#e2e8f0'}`,
                background: cfg.enabled ? '#f0fdf4' : '#f8fafc',
                flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px'
            }}>
                <div>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: cfg.enabled ? '#065f46' : '#334155' }}>
                        {cfg.enabled ? '✅ Canadian Tax Collection: ENABLED' : '○ Canadian Tax Collection: DISABLED'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '3px' }}>
                        When enabled, tax is calculated at checkout for Canadian customers and shown in the order summary.
                    </div>
                </div>
                <button
                    onClick={() => update({ enabled: !cfg.enabled })}
                    style={{
                        background: cfg.enabled ? '#ef4444' : '#10b981',
                        color: '#fff', border: 'none', padding: '10px 22px',
                        borderRadius: '7px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)', whiteSpace: 'nowrap'
                    }}
                >
                    {cfg.enabled ? 'Disable Tax Module' : 'Enable Tax Module'}
                </button>
            </div>

            {/* Store Configuration */}
            <div style={sectionCard}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#0f172a', fontWeight: '700' }}>🏪 Store Registration</h4>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                    <div>
                        <label style={labelStyle}>Store / Business Province (Nexus)</label>
                        <select value={cfg.storeProvince} onChange={e => update({ storeProvince: e.target.value })} style={inputStyle}>
                            {PROVINCES.map(p => (
                                <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
                            ))}
                        </select>
                        {storeRates.taxType && (() => {
                            const b = TAX_TYPE_BADGE[storeRates.taxType];
                            return <span style={{ display: 'inline-block', marginTop: '6px', background: b.bg, color: b.color, border: `1px solid ${b.border}`, padding: '2px 10px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '700' }}>{b.label}</span>;
                        })()}
                    </div>

                    <div>
                        <label style={labelStyle}>GST/HST Registration Number (optional)</label>
                        <input
                            type="text"
                            placeholder="e.g. 123456789 RT0001"
                            value={cfg.registrationNumber}
                            onChange={e => update({ registrationNumber: e.target.value })}
                            style={inputStyle}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px', display: 'block' }}>Displayed on order confirmations.</span>
                    </div>
                </div>
            </div>

            {/* Collection Rules */}
            <div style={sectionCard}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#0f172a', fontWeight: '700' }}>📋 Collection Rules</h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[
                        { key: 'applyToAllProvinces',      label: 'Collect tax for all Canadian provinces & territories',    desc: 'Recommended. Tax is calculated for every Canadian shipping address.' },
                        { key: 'applyOnlyInStoreProvince', label: 'Collect tax only when customer is in the store province',  desc: 'Useful for small businesses below the $30,000 GST registration threshold.' },
                        { key: 'showTaxBreakdown',         label: 'Show tax line-item breakdown in cart summary',             desc: 'Displays individual GST / HST / PST / QST rows. If off, shows one combined "Tax" line.' },
                        { key: 'taxIncludedInPrice',       label: 'Prices are tax-inclusive (back-calculate tax from price)', desc: 'When ON, product prices already include tax; tax is extracted rather than added.' },
                    ].map(({ key, label, desc }) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', padding: '10px 12px', background: '#fff', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                            <input
                                type="checkbox"
                                checked={!!cfg[key]}
                                onChange={e => {
                                    if (key === 'applyToAllProvinces' && e.target.checked) update({ applyToAllProvinces: true, applyOnlyInStoreProvince: false });
                                    else if (key === 'applyOnlyInStoreProvince' && e.target.checked) update({ applyOnlyInStoreProvince: true, applyToAllProvinces: false });
                                    else update({ [key]: e.target.checked });
                                }}
                                style={{ marginTop: '2px', accentColor: '#3b82f6', flexShrink: 0 }}
                            />
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1e293b' }}>{label}</div>
                                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>{desc}</div>
                            </div>
                        </label>
                    ))}
                </div>

                <div>
                    <label style={labelStyle}>Rounding Mode</label>
                    <select value={cfg.roundingMode} onChange={e => update({ roundingMode: e.target.value })} style={{ ...inputStyle, width: 'auto', minWidth: '200px' }}>
                        <option value="standard">Standard (nearest cent)</option>
                        <option value="up">Always round up</option>
                        <option value="down">Always round down</option>
                    </select>
                </div>
            </div>

            {/* Live Preview Calculator */}
            <div style={{ ...sectionCard, background: '#0f172a', border: '1px solid #1e293b' }}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#f1f5f9', fontWeight: '700' }}>🧮 Live Tax Preview Calculator</h4>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>See what a $100.00 subtotal would generate for any customer province, using your current settings.</p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                        <label style={{ ...labelStyle, color: '#94a3b8' }}>Customer Province</label>
                        <select value={previewProv} onChange={e => setPreviewProv(e.target.value)} style={{ ...inputStyle, background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155', width: 'auto' }}>
                            {PROVINCES.map(p => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
                        </select>
                    </div>
                    <div style={{ marginTop: '18px' }}>
                        <span style={{ background: previewBadge.bg, color: previewBadge.color, border: `1px solid ${previewBadge.border}`, padding: '3px 10px', borderRadius: '10px', fontSize: '0.78rem', fontWeight: '700' }}>
                            {previewBadge.label}
                        </span>
                    </div>
                </div>

                <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.85rem' }}>
                        <span>Subtotal</span><span>$100.00</span>
                    </div>
                    {previewResult.appliesTax && cfg.enabled ? (
                        previewResult.breakdown.map((b, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#60a5fa', fontSize: '0.85rem' }}>
                                <span>{b.label}</span><span>+${b.amount.toFixed(2)}</span>
                            </div>
                        ))
                    ) : (
                        <div style={{ color: '#64748b', fontSize: '0.85rem' }}>— No tax applies (module {cfg.enabled ? 'enabled, check rules' : 'disabled'})</div>
                    )}
                    <div style={{ borderTop: '1px solid #334155', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontWeight: '800', fontSize: '1rem', color: '#f1f5f9' }}>
                        <span>Order Total</span>
                        <span>${(100 + (cfg.enabled ? previewResult.total : 0)).toFixed(2)}</span>
                    </div>
                    {previewResult.appliesTax && cfg.enabled && (
                        <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Effective rate: {previewResult.effectiveRate}%</div>
                    )}
                </div>
            </div>

            {/* Province Rate Reference Table */}
            <div style={sectionCard}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#0f172a', fontWeight: '700' }}>📊 Province Rate Reference & Overrides</h4>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>CRA rates are pre-loaded. Use the override fields only if a rate has changed since your last engine update.</p>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9', color: '#475569', fontWeight: '700' }}>
                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Province</th>
                                <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                                <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Rate</th>
                                <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Override Rate %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {PROVINCES.map((p, i) => {
                                const r = PROVINCE_TAX_RATES[p.code];
                                const b = TAX_TYPE_BADGE[r.taxType];
                                const combined = ((r.hst || 0) + (r.gst || 0) + (r.pst || 0) + (r.qst || 0)) * 100;
                                const override = cfg.customRates?.[p.code];
                                return (
                                    <tr key={p.code} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                        <td style={{ padding: '7px 10px', fontWeight: '600', color: '#1e293b' }}>{p.name} <span style={{ color: '#94a3b8', fontWeight: '400' }}>({p.code})</span></td>
                                        <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                            <span style={{ background: b.bg, color: b.color, border: `1px solid ${b.border}`, padding: '1px 7px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '700' }}>{b.label}</span>
                                        </td>
                                        <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#0f172a', fontWeight: '600' }}>
                                            {combined.toFixed(3).replace(/\.?0+$/, '')}%
                                        </td>
                                        <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                max="30"
                                                step="0.001"
                                                placeholder="—"
                                                value={override ? ((override.hst || 0) + (override.gst || 0) + (override.pst || 0) + (override.qst || 0)) * 100 || '' : ''}
                                                onChange={e => updateCustomRate(p.code, r.taxType === 'hst' ? 'hst' : 'gst', parseFloat(e.target.value) / 100)}
                                                style={{ width: '75px', padding: '4px 8px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '0.8rem', textAlign: 'center' }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Save */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', paddingTop: '6px' }}>
                <button
                    onClick={handleSave}
                    style={{
                        background: '#0f172a', color: '#fff', border: 'none',
                        padding: '12px 26px', borderRadius: '8px', fontWeight: '700',
                        fontSize: '0.95rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                    }}
                >
                    💾 Save Tax Settings
                </button>
                {saved && <span style={{ color: '#10b981', fontWeight: '700', fontSize: '0.9rem' }}>{saved}</span>}
            </div>
        </div>
    );
};

export default CanadianTaxManager;
